/**
 * Sugerencias mientras se escribe (BUS-02).
 *
 * No usa `usePeticion` a proposito, aunque sea el hook de carga del sitio. Ese
 * esta hecho para una pantalla que pide sus datos y muestra "cargando…"; esto
 * es lo contrario: se dispara en cada tecla y el peor comportamiento posible es
 * parpadear. Mientras llega lo nuevo se sigue mostrando lo anterior, y un fallo
 * de red no pinta ningun error — cierra el panel y ya, porque el comprador
 * todavia puede apretar Enter y buscar igual.
 *
 * Los hooks llevan el prefijo `use` (y no `usar`) porque es lo que exigen las
 * reglas de React; el resto del codigo se mantiene en espanol.
 */
import { useEffect, useState } from 'react';

import type { SugerenciasBusqueda } from '@elcarpintero/compartido';

import { sugerencias as pedirSugerencias } from '../servicios/busqueda.servicio';

/**
 * Cuanto se espera despues de la ultima tecla.
 *
 * Escribir "bisagra" son siete teclas; sin esperar serian siete consultas para
 * mostrar una sola lista. 150 ms es mas rapido de lo que se escribe una letra y
 * mas lento de lo que se tarda en escribir dos seguidas.
 */
const ESPERA_MS = 150;

/**
 * Desde cuantas letras se pregunta.
 *
 * Son tres porque son las que el buscador necesita para calzar el comienzo de
 * una palabra (`busqueda-texto/terminos.ts`): con dos no encuentra nada por
 * definicion, y el panel contestaria "no tenemos ese producto" cuando en
 * realidad todavia no se sabe que esta buscando. Preguntar antes es garantizar
 * una respuesta equivocada.
 */
const LARGO_MINIMO = 3;

export function useSugerencias(termino: string, activo: boolean): SugerenciasBusqueda | null {
  const [datos, setDatos] = useState<SugerenciasBusqueda | null>(null);
  const buscable = termino.trim();

  useEffect(() => {
    if (!activo || buscable.length < LARGO_MINIMO) {
      setDatos(null);
      return;
    }

    const control = new AbortController();
    const reloj = setTimeout(() => {
      pedirSugerencias(buscable, control.signal)
        .then((nuevas) => {
          if (!control.signal.aborted) setDatos(nuevas);
        })
        .catch(() => {
          // Sin sugerencias el buscador sigue funcionando: se escribe y se
          // aprieta Enter. Un cartel de error en un panel que aparece solo
          // seria mucho mas molesto que no ver el panel.
          if (!control.signal.aborted) setDatos(null);
        });
    }, ESPERA_MS);

    // Al cambiar el termino se cancela la peticion anterior: sin esto, la
    // respuesta de "bisa" puede llegar despues de la de "bisagra" y pisarla.
    return () => {
      clearTimeout(reloj);
      control.abort();
    };
  }, [buscable, activo]);

  return datos;
}
